import { FC, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowRightIcon } from "@heroicons/react/24/outline";
import CommentListing from "components/CommentListing/CommentListing";
import FiveStartIconForRate from "components/FiveStartIconForRate/FiveStartIconForRate";
import StartRating from "components/StartRating/StartRating";
import Avatar from "shared/Avatar/Avatar";
import Badge from "shared/Badge/Badge";
import LikeSaveBtns from "components/LikeSaveBtns";
import LocationsMap from "components/GoogleMaps/LocationsMap";
import ImageMosaicGallery from "components/Quasi/ImageMosaicGallery/ImageMosaicGallery";
import { useNavigate } from "react-router-dom";
import { setBooking } from "application/features/bookings/bookingsSlice";
import { selectCheckIn, selectCheckOut } from "application/features/search/searchSlice";
import { useGetExperienceQuery } from "application/features/experience/experienceApiSlice";
import { selectCurrentToken } from "application/features/clientAuth/authClientSlice";
import { setExperience } from "application/features/experience/experienceSlice";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import ButtonCircle from "shared/Button/ButtonCircle";
import Input from "shared/Input/Input";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import DetailPagetLayout from "../Common/Layout";
import ExperienceDateInput from "../Common/ExperienceDateInput";
import GuestsInput from "../Common/GuestsInput";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { MoodEnum, StockEnum } from "data/enums";
import type { GuestsObject, BookingDataType } from "data/interfaces";
import {
  formatPrice,
  calculatePriceNumeric,
} from "shared/AppFunctions/formatPrice";
import moment from "moment";
import {
  selectGuests,
  selectGuestsCount,
} from "application/features/guests/guestsSlice";
import { useTranslation } from "react-i18next";
import returnMoodColor from "shared/AppFunctions/returnMoodColor";

interface ListingExperiencesDetailProps {}

const ListingExperiencesDetail: FC<ListingExperiencesDetailProps> = () => {
  // STATES
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState<boolean>(true);
  const [showFooterSticky, setShowFooterSticky] = useState(true);

  const [isOpenModalAmenities, setIsOpenModalAmenities] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isImageFullScreen, setIsImageFullScreen] = useState("");
  const [totalFormattedPrice, setTotalFormattedPrice] = useState<string>("0");

  // PARAMS
  const id = useParams().id;

  // HOOKS
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchCheckIn = useSelector(selectCheckIn);
  const searchCheckOut = useSelector(selectCheckOut);

  // QUERIES
  const {
    data: listing,
    isLoading,
    isSuccess,
    isError,
  } = useGetExperienceQuery(id);
  // const [fetchReviews, { data: reviews, isLoading: isLoadingReviews, isSuccess: isSuccessReviews }] = useLazyGetReviewByPropertyUUIDQuery();

  // SELECTORS
  // const experiencesList = useSelector(selectExperiencesList);
  const client_token = useSelector(selectCurrentToken);
  const guests: GuestsObject = useSelector(selectGuests);
  const guestCount: number = useSelector(selectGuestsCount);

  // TRANSLATIONS
  const { t, ready } = useTranslation("translation");

  useEffect(() => {
    // fetchReviews({ property_uuid: id, page: 1 });
  }, [id]);

  const handleOpenModalImageGallery = () => {
    // router(`${thisPathname}/?modal=PHOTO_TOUR_SCROLLABLE`);
  };

  const handleSave = (experience_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };
  const handleShare = (experience_uuid: string) => {
    toast.info("Funzionalità in arrivo");
  };

  useEffect(() => {
    // if (listing && isSuccess && !isLoading && !isSuccessExperiencesNearby) {
    //   handleFetchExperiences();
    // }
    if (!isLoading && isSuccess) {
      dispatch(setExperience(listing.experience));
    }
  }, [isSuccess]);

  useEffect(() => {
    if (searchCheckIn && searchCheckOut) {
      setStartDate(moment(searchCheckIn).toDate());
      setEndDate(moment(searchCheckOut).toDate());
      setShowDatePicker(false);
    }
  }, []);

  useEffect(() => {
    // Calculate per-person price with commission, then multiply by guests
    const basePrice = listing?.experience?.price || 0;
    const priceWithCommission = Math.ceil(basePrice * 1.366);
    const totalPrice = priceWithCommission * (guestCount || 1);
    const formattedPrice = formatPrice(
      totalPrice,
      false,
      window.navigator.language,
      listing?.experience?.currency || "EUR",
    );
    setTotalFormattedPrice(formattedPrice);
  }, [guestCount, listing]);

  useEffect(() => {
    if (listing && listing.experience) {
      document.title = listing.experience.name;
    }
  }, [listing]);

  useEffect(() => {
    // if listing is undefined return then navigate to not found page
    if (isError && !isLoading) {
      navigate("/404");
    }
  }, [isError, isLoading]);

  useEffect(() => {
    if (listing?.experience?.in_review) {
      navigate("/in-review", {
        state: { type: "experience", name: listing.experience.name },
        replace: true,
      });
    }
  }, [listing]);

  if (!ready) return null;

  const handleStartBooking = async (skipClientControl: boolean) => {
    // set booking data to redux
    if (!client_token && !skipClientControl) {
      setShowLoginModal(true);
    } else {
      setShowLoginModal(false);
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 500);

      const maxGuests = listing.experience?.max_guests;
      if (maxGuests && guestCount > maxGuests) {
        toast.error(
          `Questa esperienza può ospitare al massimo ${maxGuests} ospiti`,
        );
        return;
      } else if (guestCount <= 0) {
        toast.error(`Numero di ospiti non valido`);
        return;
      }

      const bookingData: BookingDataType = {
        id: `${listing.experience?.experience_uuid}-${moment().format("YYYYMMDDHHmmss")}`,
        item: listing.experience,
        type: StockEnum.EXPERIENCE,
        startDate: startDate
          ? moment(startDate).format("YYYY-MM-DDTHH:mm:ss")
          : "",
        endDate: endDate ? moment(endDate).format("YYYY-MM-DDTHH:mm:ss") : "",
        guests: guestCount,
        price: listing?.experience?.price * (guestCount || 1) || 0,
        additional_fees: 0,
      };
      dispatch(setBooking(bookingData));
      setShowFooterSticky(false);
      toast.success("Prenotazione aggiunta al tuo viaggio");
    }
  };

  const primaryInfoSection = () => {
    return (
      <div className="listingSection__wrap !space-y-6">
        {/* 1 */}
        <div className="flex justify-between items-center">
          <Badge
            color={returnMoodColor(listing?.experience?.mood as MoodEnum)}
            name={listing?.experience?.mood || ""}
          />
          <LikeSaveBtns
            actionSave={() => handleSave(listing?.experience?.experience_uuid)}
            actionShare={() =>
              handleShare(listing?.experience?.experience_uuid)
            }
          />
        </div>

        {/* 2 */}
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">
          {listing?.experience?.name || ""}
        </h2>

        {/* 3 */}
        <div className="flex items-center space-x-4">
          <StartRating
            point={listing?.experience?.rating || 0}
            reviewCount={listing?.experience?.total_reviews || 0}
          />
          <span>·</span>
          <span>
            <i className="las la-map-marker-alt"></i>
            <span className="ml-1">{listing?.experience?.address || ""}</span>
          </span>
        </div>

        {/* 4 */}
        <div className="flex items-center">
          <Avatar
            hasChecked
            sizeClass="h-10 w-10"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <span className="ml-2.5 text-neutral-500 dark:text-neutral-400">
            {`${t("translation:detailPages.common.hostedBy")} `}
            <span className="text-neutral-900 dark:text-neutral-200 font-medium">
              {listing?.host?.name || ""}
            </span>
          </span>
        </div>

        {/* 5 */}
        <div className="w-full border-b border-neutral-100 dark:border-neutral-700" />

        {/* 6 */}
        <div className="flex items-center justify-between xl:justify-start space-x-8 xl:space-x-12 text-sm text-neutral-700 dark:text-neutral-300">
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-clock text-2xl"></i>
            <span className="">{`${listing?.experience?.minutes_duration || 0} ${t("translation:detailPages.experience.durationUnit")}`}</span>
          </div>
          <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-user-friends text-2xl"></i>
            <span className="">
              {t("translation:detailPages.experience.guestsUpTo", {
                count: listing?.experience?.max_guests || 0,
              })}
            </span>
          </div>
          {/* <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 text-center sm:text-left sm:space-x-3 ">
            <i className="las la-language text-2xl"></i>
            <span className="">English, VietNames</span>
          </div> */}
        </div>
      </div>
    );
  };

  const experienceDescriptionSection = () => {
    return (
      <div className="listingSection__wrap">
        <h2 className="text-2xl font-semibold">
          {t("translation:detailPages.experience.descriptionSectionTitle")}
        </h2>
        <div className="text-neutral-6000 dark:text-neutral-300">
          <p>{listing?.experience?.description || ""}</p>
        </div>
      </div>
    );
  };

  const includedExcludedSection = () => {
    return (
      <div className="listingSection__wrap">
        <div>
          <h2 className="text-2xl font-semibold">
            {t("translation:detailPages.experience.includedSectionTitle")}
          </h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            {listing?.experience?.included || ""}
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
        <div>
          <h2 className="text-2xl font-semibold">
            {t("translation:detailPages.experience.notIncludedSectionTitle")}
          </h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            {listing?.experience?.not_included || ""}
          </span>
        </div>
      </div>
    );
  };

  const hostDetailsSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <div className="flex flex-row items-center justify-between">
          <h2 className="text-2xl font-semibold">{t("translation:detailPages.common.theHost")}</h2>
          <ButtonSecondary href={`/author/${listing?.experience?.host_uuid}`}>{t("translation:detailPages.common.goToHostProfile")}</ButtonSecondary>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* host */}
        <div className="flex items-center space-x-4">
          <Avatar
            hasChecked
            hasCheckedClass="w-4 h-4 -top-0.5 right-0.5"
            sizeClass="h-14 w-14"
            radius="rounded-full"
            imgUrl={listing?.host?.avatar || ""}
          />
          <div>
            <Link className="block text-xl font-medium break-words" to={`/author/${listing?.experience?.host_uuid}`}>
              {listing?.host?.name || ""}
            </Link>
            <div className="mt-1.5 flex items-center text-sm text-neutral-500 dark:text-neutral-400">
              <StartRating
                point={listing?.host?.rating || 0}
                reviewCount={listing?.host?.total_reviews || 0}
              />
              <span className="mx-2">·</span>
              <span> 12 places</span>
            </div>
          </div>
        </div>

        {/* desc */}
        <span className="block text-neutral-6000 dark:text-neutral-300">
          {listing?.host?.description || ""}
        </span>

        {/* info */}
        <div className="block text-neutral-500 dark:text-neutral-400 space-y-2.5">
          {listing?.host?.joined_date && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.joinedIn", {
                  date: listing?.host?.joined_date || "",
                })}
              </span>
            </div>
          )}
          {listing?.host?.response_percentage && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.hostResonseRate", {
                  percentage: listing?.host?.response_rate || "",
                })}
              </span>
            </div>
          )}
          {listing?.host?.response_time && (
            <div className="flex items-center space-x-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span>
                {t("translation:detailPages.common.hostResonseTime", {
                  time: listing?.host?.response_time || "",
                })}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const reviewsSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">Reviews (23 reviews)</h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>

        {/* Content */}
        <div className="space-y-5">
          <FiveStartIconForRate iconClass="w-6 h-6" className="space-x-0.5" />
          <div className="relative">
            <Input
              fontClass=""
              sizeClass="h-16 px-4 py-3"
              rounded="rounded-3xl"
              placeholder="Share your thoughts ..."
            />
            <ButtonCircle
              className="absolute right-2 top-1/2 transform -translate-y-1/2"
              size=" w-12 h-12 "
            >
              <ArrowRightIcon className="w-5 h-5" />
            </ButtonCircle>
          </div>
        </div>

        {/* comment */}
        <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
          <CommentListing className="py-8" />
          <CommentListing className="py-8" />
          <CommentListing className="py-8" />
          <CommentListing className="py-8" />
          <div className="pt-8">
            <ButtonSecondary>View more 20 reviews</ButtonSecondary>
          </div>
        </div>
      </div>
    );
  };

  const locationSection = () => {
    if (!listing?.experience?.latitude || !listing?.experience?.longitude)
      return null;

    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <div>
          <h2 className="text-2xl font-semibold">
            {t("translation:detailPages.common.location")}
          </h2>
          <span className="block mt-2 text-neutral-500 dark:text-neutral-400">
            {listing?.experience?.address || ""}
          </span>
        </div>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

        {/* MAP */}
        <div className="aspect-w-5 aspect-h-5 sm:aspect-h-3 ring-1 ring-black/10 rounded-xl z-0">
          <div className="rounded-xl overflow-hidden z-0">
            <LocationsMap
              className="rounded-xl"
              defaultCenter={{
                lat: Number(listing?.experience?.latitude),
                lng: Number(listing?.experience?.longitude),
              }}
              markers={[
                {
                  lat: Number(listing?.experience?.latitude),
                  lng: Number(listing?.experience?.longitude),
                },
              ]}
            />
          </div>
        </div>
      </div>
    );
  };

  const thingsToKnowSection = () => {
    return (
      <div className="listingSection__wrap">
        {/* HEADING */}
        <h2 className="text-2xl font-semibold">
          {t("translation:detailPages.common.thingsToKnow")}
        </h2>
        <div className="w-14 border-b border-neutral-200 dark:border-neutral-700" />

        {/* CONTENT */}
        <div>
          <h4 className="text-lg font-semibold">
            {t("translation:detailPages.common.whatToBringTitle")}
          </h4>
          <div className="prose sm:prose">
            {listing?.experience?.what_to_wear || ""}
          </div>
        </div>

        {/* CONTENT */}
        <div>
          <h4 className="text-lg font-semibold">{t("translation:detailPages.common.cancellationPolicyTitle")}</h4>
          <span className="block mt-3 text-neutral-500 dark:text-neutral-400">
            <Link to={"/terms-and-conditions"}>{t("translation:detailPages.common.visitTermsAndConditions") || "Visita la pagina di termini e condizioni per maggiori informazioni."}</Link>
          </span>
        </div>

      </div>
    );
  };

  const bookingActionSidebar = () => {
    return (
      <div className="listingSectionSidebar__wrap shadow-xl">
        {/* PRICE */}
        <div className="flex justify-between">
          <span className="text-3xl font-semibold">
            {formatPrice(
              listing?.experience?.price || 0,
              true,
              window.navigator.language,
              listing?.experience?.currency || "EUR",
            )}
            <span className="ml-1 text-base font-normal text-neutral-500 dark:text-neutral-400">
              /person
            </span>
          </span>
          <StartRating />
        </div>

        {/* FORM */}
        <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-3xl ">
          <ExperienceDateInput
            className="flex-1 z-[11]"
            value={startDate}
            minHour={listing?.experience?.available_from ?? 0}
            maxHour={listing?.experience?.available_to ?? 23}
            slotMinutes={60}
            onChange={(date) => {
              setStartDate(date);
              setEndDate(
                date
                  ? moment(date)
                      .add(
                        listing?.experience?.minutes_duration || 0,
                        "minutes",
                      )
                      .toDate()
                  : null,
              );
              setShowDatePicker(!date);
            }}
          />
          <div className="w-full border-b border-neutral-200 dark:border-neutral-700"></div>
          <GuestsInput className="flex-1" />
        </form>

        {/* SUM */}
        <div className="flex flex-col space-y-4">
          <div className="flex justify-between text-neutral-6000 dark:text-neutral-300">
            <span>{`${formatPrice(
              listing?.experience?.price || 0,
              true,
              window.navigator.language,
              listing?.experience?.currency || "EUR",
            )} x ${guestCount || 1}`}</span>
            <span>{totalFormattedPrice}</span>
          </div>
          <div className="border-b border-neutral-200 dark:border-neutral-700"></div>
          <div className="flex justify-between font-semibold">
            <span>Total</span>
            <span>{totalFormattedPrice}</span>
          </div>
        </div>

        {/* SUBMIT */}
        <ButtonPrimary
          onClick={() =>
            startDate
              ? handleStartBooking(false)
              : toast.error("Seleziona le data per continuare")
          }
          className="w-full py-3 sm:py-4 text-sm sm:text-base min-h-[44px] touch-manipulation"
          disabled={listing?.experience?.stopped}
          loading={loading}
        >
          {t("translation:detailPages.common.addToTrip")}
        </ButtonPrimary>
      </div>
    );
  };

  return (
    <div className={` nc-ListingExperiencesDetailPage `}>
      {/* SINGLE HEADER || Gallery */}
      <ImageMosaicGallery
        coverImage={listing?.experience?.cover || ""}
        galleryImages={listing?.experience?.media || []}
        onOpenGallery={handleOpenModalImageGallery}
        alt="Experience Gallery"
      />

      {/* MAIn */}
      <main className="relative z-10 my-11 flex flex-col lg:flex-row">
        <div className="flex flex-col px-4 lg:flex-row max-w-4xl lg:max-w-5xl xl:max-w-6xl mx-auto">
          {/* CONTENT */}
          <div className="flex flex-col w-full space-y-8 lg:w-3/5 xl:w-2/3 lg:pr-10 lg:space-y-10">
            {primaryInfoSection()}
            <div className="block lg:hidden">{bookingActionSidebar()}</div>
            {experienceDescriptionSection()}
            {includedExcludedSection()}
            {/* <SectionDateRange /> */}

            {hostDetailsSection()}
            {/* {reviewsSection()} */}
            {locationSection()}
            {thingsToKnowSection()}
          </div>

          {/* SIDEBAR */}
          <div className="hidden lg:block flex-grow mt-14 lg:mt-0">
            <div className="sticky top-28">{bookingActionSidebar()}</div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default function ListingExperiencesDetailPage() {
  return (
    <DetailPagetLayout>
      <ListingExperiencesDetail />
    </DetailPagetLayout>
  );
}
